import React, { useState, MouseEvent, ReactNode } from "react";
import {
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Button,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  Modal,
  Paper,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import {
  GridOn as GridOnIcon,
  LocationOn as LocationOnIcon,
  Factory as FactoryIcon,
  Apartment as ApartmentIcon,
  AttachMoney as AttachMoneyIcon,
  Diversity3 as Diversity3Icon,
  ConnectWithoutContact as ConnectWithoutContactIcon,
  Grading as GradingIcon,
  DeviceHub as DeviceHubIcon,
  CalendarMonth as CalendarMonthIcon,
  LocalPhone as LocalPhoneIcon,
  DomainAdd as DomainAddIcon,
  TravelExplore as TravelExploreIcon,
  FindInPage as FindInPageIcon,
  ContentPasteOff as ContentPasteOffIcon,
  TextFields as TextFieldsIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  MoneyOff as MoneyOffIcon,
  Gavel as GavelIcon,
  FilterAlt,
  PointOfSale as PointOfSaleIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useProjectService } from "../../../../../use-cases/project";
import { useUserService } from "../../../../../use-cases/user";
import { useBlackListApi } from "../../../../../infra/black-list-api";
import { IProjectResume } from "../../../../../application/states/project";
import { IProject } from "../../../../../application/models/IProject";
import { IProjectFilter } from "../../../../../application/models/IProjectFilter";
import { IFilter } from "../../../../../application/models/IProjectExportContent";
import { TransitionProps } from "@mui/material/transitions";
import { useCepListService } from "../../../../../use-cases/cep-list/useCepListService";
import { useEconomicActivityListService } from "../../../../../use-cases/economic-activity-list/useEconomicActivityListService";

import { FixedSizeList as VirtualList } from "react-window";
import { useAppSelector } from "../../../../../application/states/hooks";
import { IEconomicActivityList } from "../../../../../application/models";
import { formatCnpj } from "../../../../../utils/formatters/cnpj";

interface Props {
  project: IProjectResume;
  onOpenProject: (id: string) => void;
}

const formatNumber = (value?: number) =>
  value !== undefined && value !== null ? value.toLocaleString("pt-BR") : "—";

function formatCNPJ(cnpj: string) {
  // Usa a função centralizada do cnpjFormatter que suporta
  // tanto o formato numérico antigo quanto o alfanumérico novo (RF 2026)
  return formatCnpj(cnpj);
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const NameCellRenderer: React.FC<Props> = ({
  project,
  onOpenProject,
}) => {
  const { getProjectDetails } = useProjectService();
  const { findById: getUserById } = useUserService();
  const { findBlackListByIdApi } = useBlackListApi();
  const { findByIdCepList } = useCepListService();
  const { findEconomicActivityListById } = useEconomicActivityListService();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState<IProject | null>(null);
  const [creatorName, setCreatorName] = useState<string>("");
  const [blackListMap, setBlackListMap] = useState<Record<string, string>>({});
  const [blackListContent, setBlackListContent] = useState<
    Record<string, string[]>
  >({});
  const [cepListsDetails, setCepListsDetails] = useState<Record<string, any>>(
    {},
  );
  const [economicActivityListsDetails, setEconomicActivityListsDetails] =
    useState<Record<string, any>>({});

  const [expandedBlacklist, setExpandedBlacklist] = useState<string | null>(
    null,
  );
  const user = useAppSelector((state) => state.user);
  const allowedUserTypes = [
    "OWNER",
    "ADMIN",
    "CUSTOMER EXPERIENCE",
    "SALES SUPERVISOR",
    "SELLER",
    "OPERATOR",
  ];
  const canSeeBlacklistContent = allowedUserTypes.includes(
    user?.userType as string,
  );

  const handleOpenDetails = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOpen(true);

    getProjectDetails({ projectId: project.id })
      .then((p) => {
        setDetails(p);
        const ids = Array.from(
          new Set(p.filters.flatMap((f) => f.blackLists ?? [])),
        );
        // aqui devolvemos o próprio projeto dentro do then() do Promise.all
        return Promise.all(
          ids.map((id) =>
            findBlackListByIdApi({ blackListId: id })
              .then((bl) => ({ id, name: bl.name, list: bl.list }))
              .catch(() => ({ id, name: id, list: [] })),
          ),
        ).then((results) => {
          const map: Record<string, string> = {};
          results.forEach((r) => {
            map[r.id] = r.name;
            if (r.list) {
              setBlackListContent((prev) => ({ ...prev, [r.id]: r.list }));
            }
          });
          setBlackListMap(map);
          return p;
        });
      })
      .then((p) => getUserById({ userId: p.createdBy }))
      .then((u) => setCreatorName(u.name))
      .catch(() => {
        setDetails(null);
        setCreatorName("");
        setBlackListMap({});
      });
  };

  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    if (!open || !details || !details.filters) return;
    details.filters.forEach((f) => {
      (f.cepLists ?? []).forEach((id) => {
        if (!cepListsDetails[id]) {
          findByIdCepList({ cepListId: id }).then((cepList: any) => {
            setCepListsDetails((prev) => ({
              ...prev,
              [id]: cepList || [],
            }));
          });
        }
      });
      (f.purgeCepLists ?? []).forEach((id) => {
        if (!cepListsDetails[id]) {
          findByIdCepList({ cepListId: id }).then((cepList: any) => {
            setCepListsDetails((prev) => ({
              ...prev,
              [id]: cepList || [],
            }));
          });
        }
      });

      // NOVO: Carregar Economic Activity Lists
      (f.economicActivityList ?? []).forEach(
        (economicActivityList: IEconomicActivityList) => {
          if (!economicActivityListsDetails[economicActivityList.id]) {
            const idList = economicActivityList.id;
            findEconomicActivityListById(idList).then((list: any) => {
              setEconomicActivityListsDetails((prev) => ({
                ...prev,
                [idList]: list || [],
              }));
            });
          }
        },
      );

      (f.purgeEconomicActivityLists ?? []).forEach(
        (economicActivityList: IEconomicActivityList) => {
          if (!economicActivityListsDetails[economicActivityList.id]) {
            const idList = economicActivityList.id;
            findEconomicActivityListById(idList).then((list: any) => {
              setEconomicActivityListsDetails((prev) => ({
                ...prev,
                [idList]: list || [],
              }));
            });
          }
        },
      );
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, details]);

  const renderFilters = (filters: IFilter[]): ReactNode => (
    <Box sx={{ width: "100%" }}>
      {filters.map((f, i) => (
        <Box
          key={i}
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            bgcolor: "#fafafa",
            width: "100%",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filtros
          </Typography>
          <List dense disablePadding>
            {(f.location?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LOCALIZAÇÃO / UF / CIDADE:</strong>
                      <br />
                      {f.location?.map((l, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {l.value}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.purgeLocation?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LOCALIZAÇÃO EXCLUÍDA:</strong>
                      <br />
                      {f.purgeLocation?.map((l, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {l.value}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.cepLists?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LISTAS DE CEP:</strong>
                      {f.cepLists?.map((id) => (
                        <Box key={id} sx={{ ml: 1, display: "block" }}>
                          <Typography variant="subtitle2" color="primary">
                            {cepListsDetails[id]?.name || "Carregando..."}
                          </Typography>
                          <Box sx={{ ml: 2 }}>
                            {cepListsDetails[id]?.list ? (
                              cepListsDetails[id].list.map(
                                (cep: string, idx: number) => (
                                  <span key={idx} style={{ display: "block" }}>
                                    ° {cep}
                                  </span>
                                ),
                              )
                            ) : (
                              <Typography variant="body2">
                                Carregando...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.purgeCepLists?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ContentPasteOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>CEPS EXCLUÍDOS:</strong>
                      {f.purgeCepLists?.map((id) => (
                        <Box key={id} sx={{ ml: 1, display: "block" }}>
                          <Typography variant="subtitle2" color="primary">
                            {cepListsDetails[id]?.name || "Carregando..."}
                          </Typography>
                          <Box sx={{ ml: 2 }}>
                            {cepListsDetails[id]?.list ? (
                              cepListsDetails[id].list.map(
                                (cep: string, idx: number) => (
                                  <span key={idx} style={{ display: "block" }}>
                                    ° {cep}
                                  </span>
                                ),
                              )
                            ) : (
                              <Typography variant="body2">
                                Carregando...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.useGeolocationFilter != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>FILTRO DE GEOLOCALIZAÇÃO:</strong>{" "}
                      {f.useGeolocationFilter ? "Sim" : "Não"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.geolocation && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>GEOLOCALIZAÇÃO:</strong> lat {f.geolocation.lat},
                      lon {f.geolocation.lon}, raio {f.geolocation.radius}{" "}
                      km{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.filterByLatLong != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>FILTRAR POR LATITUDE/LONGITUDE:</strong>{" "}
                      {f.filterByLatLong ? "Sim" : "Não"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.cepFilterByLatLong && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>CEPS PARA GEOLOCALIZAÇÃO:</strong>{" "}
                      {f.cepFilterByLatLong}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.radius != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TravelExploreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>RAIO (km):</strong> {f.radius}{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.sector?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <FactoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SETOR ECONÔMICO:</strong>
                      <br />
                      {f.sector?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.segmentos?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <GridOnIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SEGMENTOS / GRUPOS:</strong>
                      <br />
                      {f.segmentos?.map((s, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {s.segmento}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.justPrimaryActivity != null && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>INCLUIR APENAS ATIVIDADE PRIMÁRIA:</strong>{" "}
                      {f.justPrimaryActivity ? "SIM" : "NÃO"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.economicActivity?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>ATIVIDADE ECONÔMICA / CNAE:</strong>
                      <br />
                      {f.economicActivity?.map((a, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {a.cnaeDescricao}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {/* LISTAS DE ATIVIDADE ECONÔMICA */}
            {(f.economicActivityList?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ApartmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LISTAS DE ATIVIDADE ECONÔMICA:</strong>
                      {f.economicActivityList?.map(
                        (item: IEconomicActivityList) => (
                          <Box key={item.id} sx={{ ml: 1, display: "block" }}>
                            <Typography variant="subtitle2" color="primary">
                              {economicActivityListsDetails[item.id]?.name ||
                                "Carregando... "}
                            </Typography>
                            <Box sx={{ ml: 2 }}>
                              {economicActivityListsDetails[item.id]?.list ? (
                                economicActivityListsDetails[item.id].list.map(
                                  (cnae: any, idx: number) => (
                                    <span
                                      key={idx}
                                      style={{ display: "block" }}
                                    >
                                      °{" "}
                                      {cnae.cnaeDescricao ||
                                        cnae.codigo ||
                                        cnae}
                                    </span>
                                  ),
                                )
                              ) : (
                                <Typography variant="body2">
                                  
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ),
                      )}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.purgeEconomicActivityLists?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <ContentPasteOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>LISTAS DE ATIVIDADE ECONÔMICA (DEDUPLICAÇÃO):</strong>
                      {f.purgeEconomicActivityLists?.map(
                        (item: IEconomicActivityList) => (
                          <Box key={item.id} sx={{ ml: 1, display: "block" }}>
                            <Typography variant="subtitle2" color="primary">
                              {economicActivityListsDetails[item.id]?.name ||
                                "Carregando..."}
                            </Typography>
                            <Box sx={{ ml: 2 }}>
                              {economicActivityListsDetails[item.id]?.list ? (
                                economicActivityListsDetails[item.id].list.map(
                                  (cnae: any, idx: number) => (
                                    <span
                                      key={idx}
                                      style={{ display: "block" }}
                                    >
                                      °{" "}
                                      {cnae.cnaeDescricao ||
                                        cnae.codigo ||
                                        cnae}
                                    </span>
                                  ),
                                )
                              ) : (
                                <Typography variant="body2">
                                  
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ),
                      )}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.includeBusinessSize?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <DomainAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PORTE DA EMPRESA:</strong>
                      <br />
                      {f.includeBusinessSize?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.notIncludeBusinessSize?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <DomainAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PORTE DA EMPRESA EXCLUÍDA:</strong>
                      <br />
                      {f.notIncludeBusinessSize?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.revenues &&
              (f.revenues.min != null || f.revenues.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <AttachMoneyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <strong>FATURAMENTO:</strong>{" "}
                        {f.revenues.min != null
                          ? `R$ ${formatNumber(f.revenues.min)}`
                          : "—"}{" "}
                        a{" "}
                        {f.revenues.max != null
                          ? `R$ ${formatNumber(f.revenues.max)}`
                          : "—"}
                      </>
                    }
                  />
                </ListItem>
              )}
            {f.staff?.employee &&
              (f.staff.employee.min != null ||
                f.staff.employee.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <Diversity3Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        {" "}
                        <strong>NÚMERO DE FUNCIONÁRIOS:</strong>{" "}
                        {formatNumber(f.staff.employee.min)} a{" "}
                        {formatNumber(f.staff.employee.max)}{" "}
                      </>
                    }
                  />
                </ListItem>
              )}
            {f.staff?.businessAssociate &&
              (f.staff.businessAssociate.min != null ||
                f.staff.businessAssociate.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <ConnectWithoutContactIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        {" "}
                        <strong>NÚMERO DE SÓCIOS:</strong>{" "}
                        {formatNumber(f.staff.businessAssociate.min)} a{" "}
                        {formatNumber(f.staff.businessAssociate.max)}{" "}
                      </>
                    }
                  />
                </ListItem>
              )}
            {f.shareCapital &&
              (f.shareCapital.min != null || f.shareCapital.max != null) && (
                <ListItem disableGutters>
                  <ListItemIcon>
                    <CurrencyExchangeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <strong>CAPITAL SOCIAL:</strong>{" "}
                        {f.shareCapital.min != null
                          ? `R$ ${formatNumber(f.shareCapital.min)}`
                          : "—"}{" "}
                        a{" "}
                        {f.shareCapital.max != null
                          ? `R$ ${formatNumber(f.shareCapital.max)}`
                          : "—"}
                      </>
                    }
                  />
                </ListItem>
              )}
            {(f.economicGroup?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <DeviceHubIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>MATRIZ / FILIAL:</strong>
                      <br />
                      {f.economicGroup?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.status?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <GradingIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SITUAÇÃO CADASTRAL:</strong>
                      <br />
                      {f.status?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.createdAt?.min != null || f.createdAt?.max != null) && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <CalendarMonthIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>DATA DE ABERTURA:</strong>{" "}
                      {f.createdAt.min
                        ? new Date(f.createdAt.min).toLocaleDateString()
                        : "—"}{" "}
                      e{" "}
                      {f.createdAt.max
                        ? new Date(f.createdAt.max).toLocaleDateString()
                        : "—"}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.termsFilter?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TextFieldsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PALAVRAS CHAVE:</strong>
                      <br />
                      {f.termsFilter?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.notIncludeTermsFilter?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <TextFieldsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PALAVRAS CHAVE EXCLUÍDAS:</strong>
                      <br />
                      {f.notIncludeTermsFilter?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.creditScoreDescriptions?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <MoneyOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>
                        PENDÊNCIAS FINANCEIRAS / Saúde empresarial:
                      </strong>
                      <br />
                      {f.creditScoreDescriptions?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.includeActiveDebts === true && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <MoneyOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>
                        PENDÊNCIAS FINANCEIRAS / Dívida Ativa:
                      </strong>{" "}
                      {f.includeActiveDebts === true ? "Sim" : "Não"}{" "}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.debts?.debt?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <MoneyOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>PENDÊNCIAS FINANCEIRAS:</strong>
                      <br />
                      {f.debts?.debt?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {f.field && f.value && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <FindInPageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {" "}
                      <strong>CAMPO PERSONALIZADO:</strong> {f.field}:{" "}
                      {f.value}{" "}
                    </>
                  }
                />
              </ListItem>
            )}

            {(f.specialStatus?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <FindInPageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>SITUAÇÃO ESPECIAL:</strong>{" "}
                      {f.specialStatus?.includes("NOT_INCLUDE")
                        ? "Não incluso"
                        : f.specialStatus?.join(", ")}
                    </>
                  }
                />
              </ListItem>
            )}
            {(f.regimeTributario?.length ?? 0) > 0 && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <PointOfSaleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>REGIME TRIBUTÁRIO:</strong>
                      <br />
                      {f.regimeTributario?.map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}
            {((f.naturezaJuridica?.include?.length ?? 0) > 0 ||
              (f.naturezaJuridica?.notInclude?.length ?? 0) > 0) && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <GavelIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {(f.naturezaJuridica?.include?.length ?? 0) > 0 && (
                        <>
                          <strong>NATUREZA JURÍDICA (incluir):</strong>
                          <br />
                          {f.naturezaJuridica?.include?.map((n, idx) => (
                            <span key={idx} style={{ display: "block" }}>
                              ° {n.descricao}
                            </span>
                          ))}
                          <br />
                        </>
                      )}
                      {(f.naturezaJuridica?.notInclude?.length ?? 0) > 0 && (
                        <>
                          <strong>NATUREZA JURÍDICA (excluir):</strong>
                          <br />
                          {f.naturezaJuridica?.notInclude?.map((n, idx) => (
                            <span key={idx} style={{ display: "block" }}>
                              ° {n.descricao}
                            </span>
                          ))}
                        </>
                      )}
                    </>
                  }
                />
              </ListItem>
            )}
            {f.contacts && (
              <ListItem disableGutters>
                <ListItemIcon>
                  <LocalPhoneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>CONTATOS (Empresas):</strong>
                      <br />
                      {(f.contacts.business ?? []).map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                      <br />
                      <strong>CONTATOS (Sócios):</strong>
                      <br />
                      {(f.contacts.partner ?? []).map((item, idx) => (
                        <span key={idx} style={{ display: "block" }}>
                          ° {item}
                        </span>
                      ))}
                    </>
                  }
                />
              </ListItem>
            )}

            {/* { f.justPrimaryActivity != null && (
              <ListItem disableGutters>
                <ListItemIcon><FactoryIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={<> <strong>ATIVIDADE ECONÔMICA:</strong> {f.justPrimaryActivity ? 'Sim' : 'Não'} </>}
                />
              </ListItem>
            ) } */}

            {(f.blackLists?.length ?? 0) > 0 && (
              <ListItem
                disableGutters
                sx={{ flexDirection: "column", alignItems: "flex-start" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ListItemIcon>
                    <ContentPasteOffIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                    DEDUPLICAÇÃO:
                  </Typography>
                </Box>
                {Array.from(new Set(f.blackLists)).map((id) => (
                  <Accordion
                    key={id + "-accordion"}
                    expanded={expandedBlacklist === id}
                    onChange={() =>
                      setExpandedBlacklist(expandedBlacklist === id ? null : id)
                    }
                    sx={{
                      width: "100%",
                      bgcolor: "#f9f9f9",
                      boxShadow: "none",
                      border: "1px solid #eee",
                      mb: 1,
                    }}
                    TransitionProps={{ unmountOnExit: true }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        {blackListMap[id] ?? id}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        Lista de deduplicação: (
                        {formatNumber((blackListContent[id] ?? []).length)})
                      </Typography>
                      {!canSeeBlacklistContent ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            my: 2,
                            textAlign: "center",
                          }}
                        >
                          Visualização restrita
                        </Typography>
                      ) : (blackListContent[id] ?? []).length > 0 ? (
                        <VirtualList
                          height={240}
                          itemCount={blackListContent[id].length}
                          itemSize={44}
                          width="100%"
                        >
                          {({ index, style }) => (
                            <ListItem
                              style={style}
                              key={blackListContent[id][index] + index}
                              sx={{
                                py: 0.5,
                                bgcolor:
                                  index % 2 === 0 ? "#f5f5f5" : "#e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              <ListItemText
                                primary={formatCNPJ(
                                  blackListContent[id][index],
                                )}
                              />
                            </ListItem>
                          )}
                        </VirtualList>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            my: 2,
                            textAlign: "center",
                          }}
                        >
                          Nenhum encontrado
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </ListItem>
            )}
          </List>
        </Box>
      ))}
    </Box>
  );
  return (
    <Box display="flex" alignItems="center">
      <Typography
        variant="body2"
        onClick={() => onOpenProject(project.id)}
        sx={{ cursor: "pointer" }}
      >
        {project.name}
      </Typography>
      <IconButton size="small" onClick={handleOpenDetails} sx={{ ml: 0.5 }}>
        <ExpandMoreIcon fontSize="small" />
      </IconButton>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Detalhes do Projeto
            </Typography>
            <Button autoFocus color="inherit" onClick={handleClose}>
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            p: 4,
            maxWidth: "100vw",
            maxHeight: "100vh",
            overflowY: "auto",
            bgcolor: "background.paper",
          }}
        >
          {!details ? (
            <Typography sx={{ p: 2 }}>Carregando…</Typography>
          ) : (
            <List dense sx={{ minWidth: 320 }}>
              <ListItem>
                <ListItemText primary="Nome" secondary={details.name} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Criado por"
                  secondary={creatorName || details.createdBy}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Data de criação"
                  secondary={new Date(details.createdAt).toLocaleString()}
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary=""
                  secondary={
                    details.filters?.length
                      ? renderFilters([
                          details.filters[details.filters.length - 1],
                        ])
                      : "Nenhum filtro"
                  }
                />
              </ListItem>
            </List>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};
